/**
 * Shared ArcGIS map utilities for SiteDetailMap and CreateProjectMap.
 * Uses AMD (window.require) for esri modules.
 */

import axios from '../api/axiosConfig';
import { mapColors } from './themeColors';
import { getServiceUrl } from './appSettings';
import { nyc1940sPhotoUrl } from './urlHelpers';

// Default center: 253 Broadway, downtown Manhattan
const NYC_CENTER = { longitude: -74.0071, latitude: 40.7128 };
const NYC_ZOOM = 15;

/** Load a single AMD module via window.require, returning a Promise. */
export const loadAMD = (name) => new Promise((res, rej) => {
  window.require([name], (m) => res(m), (err) => rej(err));
});

// Preload common AMD modules so they're cached before first map render.
// Call early (e.g. on app mount or route change) — safe to call multiple times.
let _preloadPromise = null;
export function preloadArcGISModules() {
  if (_preloadPromise) return _preloadPromise;
  _preloadPromise = new Promise((resolve) => {
    // Wait for Dojo loader to be available
    const check = () => {
      if (window.require) {
        const modules = [
          'esri/Map', 'esri/views/MapView', 'esri/config',
          'esri/widgets/Fullscreen', 'esri/Graphic',
          'esri/layers/FeatureLayer', 'esri/geometry/Point',
          'esri/geometry/Extent', 'esri/identity/IdentityManager'
        ];
        Promise.all(modules.map(loadAMD)).then(() => resolve()).catch(() => resolve());
      } else {
        setTimeout(check, 50);
      }
    };
    check();
  });
  return _preloadPromise;
}

// Cached ArcGIS token (fetched from backend to keep API keys server-side).
// _ensureTokenPromise is the single shared promise that all callers await,
// eliminating the race condition where parallel calls could double-register.
let _tokenRegistered = false;
let _ensureTokenPromise = null;

function fetchArcGISToken() {
  return axios.get('/api/v1/proxy/arcgis-token')
    .then(res => {
      const d = res.data?.data;
      return d?.token ? { token: d.token, expires: d.expires || null } : null;
    })
    .catch(() => null);
}

/**
 * Fetch an ArcGIS token from the backend and register it with IdentityManager.
 * Safe to call concurrently — all callers share a single promise so the token
 * is fetched and registered exactly once.
 * @returns {Promise<void>}
 */
export function ensureTokenRegistered() {
  if (_tokenRegistered) return Promise.resolve();
  if (!_ensureTokenPromise) {
    _ensureTokenPromise = (async () => {
      const IdentityManager = await loadAMD('esri/identity/IdentityManager');
      const result = await fetchArcGISToken();
      if (result) {
        const tokenInfo = { token: result.token, ...(result.expires ? { expires: result.expires } : {}) };
        // Register for ArcGIS Online portal (basemap style lookups, portal item access)
        IdentityManager.registerToken({ server: 'https://www.arcgis.com/sharing', ...tokenInfo });
        // Register for basemap tile services (vector tile basemaps like streets-vector)
        IdentityManager.registerToken({ server: 'https://basemaps-api.arcgis.com', ...tokenInfo });
        // Register for geocoding service (address search, suggestions)
        IdentityManager.registerToken({ server: 'https://geocode.arcgis.com', ...tokenInfo });
        _tokenRegistered = true;
      }
    })().catch(() => {
      // Reset so a future call can retry after a transient failure
      _ensureTokenPromise = null;
    });
  }
  return _ensureTokenPromise;
}

/**
 * Create a MapView with NYC extent. Retries until container and window.require are ready.
 * @param {HTMLElement | null} containerEl
 * @param {object} [opts] - reserved for future options
 * @returns {Promise<{ view: import('esri/views/MapView').default, destroy: () => void }>}
 */
export function createMapView(containerEl, opts = {}) {
  return new Promise((resolve, reject) => {
    let retries = 0;
    let cancelled = false;

    // Allow callers to abort the init (e.g. on component unmount).
    // opts.signal is an AbortSignal — when aborted, retries stop and the
    // promise rejects so the caller's cleanup can proceed without leaking.
    if (opts.signal) {
      if (opts.signal.aborted) { reject(new Error('ArcGIS: aborted')); return; }
      opts.signal.addEventListener('abort', () => { cancelled = true; }, { once: true });
    }

    const tryInit = async () => {
      if (cancelled) { reject(new Error('ArcGIS: aborted')); return; }
      retries++;
      if (!containerEl || !window.require) {
        if (retries < 30) setTimeout(tryInit, 100);
        else reject(new Error('ArcGIS: container or require not ready'));
        return;
      }

      // Wait for container to have non-zero dimensions before creating the view.
      // ArcGIS MapView requires a sized container to render properly.
      const rect = containerEl.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) {
        if (retries < 50) {
          // Force minimum dimensions if stuck after 3 seconds
          if (retries === 30) {
            containerEl.style.height = '400px';
            containerEl.style.minHeight = '400px';
          }
          setTimeout(tryInit, 100);
        } else {
          reject(new Error('ArcGIS: container has zero dimensions'));
        }
        return;
      }

      const webmapId = process.env.REACT_APP_WEBMAP_ITEM_ID;
      const baseModules = webmapId
        ? ['esri/WebMap', 'esri/views/MapView', 'esri/config', 'esri/widgets/Fullscreen']
        : ['esri/Map', 'esri/views/MapView', 'esri/config', 'esri/widgets/Fullscreen'];
      // Load LayerList + Expand for WebMap-based maps (unless opted out)
      const showLayerList = webmapId && opts.layerList !== false;
      const modules = showLayerList
        ? [...baseModules, 'esri/widgets/LayerList', 'esri/widgets/Expand']
        : baseModules;

      // Load AMD modules and register token in parallel
      try {
        const [moduleResults] = await Promise.all([
          Promise.all(modules.map(loadAMD)),
          ensureTokenRegistered()
        ]);
        const [MapOrWebMap, MapView, , Fullscreen, LayerList, Expand] = moduleResults;

        if (cancelled || !containerEl) {
          reject(new Error('ArcGIS: ' + (cancelled ? 'aborted' : 'container unmounted')));
          return;
        }

        const map = webmapId
          ? new MapOrWebMap({ portalItem: { id: webmapId } })
          : new MapOrWebMap({ basemap: 'streets-vector' });

        const initCenter = opts.center || [NYC_CENTER.longitude, NYC_CENTER.latitude];
        const initZoom = opts.zoom != null ? opts.zoom : NYC_ZOOM;
        const view = new MapView({ container: containerEl, map, center: initCenter, zoom: initZoom });

        // Remove default zoom widget to control ordering (layers first)
        view.ui.remove('zoom');

        // 1. Add LayerList widget first (before zoom) for WebMap-based maps
        let layerListExpand;
        if (showLayerList && LayerList && Expand) {
          const layerList = new LayerList({ view });
          layerListExpand = new Expand({
            view,
            content: layerList,
            expandIcon: 'layers',
            expandTooltip: 'Layers',
            group: 'top-left'
          });
          view.ui.add(layerListExpand, 'top-left');
        }

        // 2. Re-add zoom widget after layers
        view.ui.add('zoom', 'top-left');

        // 3. Add fullscreen last
        const fullscreen = new Fullscreen({ view, element: opts.fullscreenElement || undefined });
        view.ui.add(fullscreen, 'top-left');

        const destroy = () => {
          try {
            if (layerListExpand) layerListExpand.destroy();
            fullscreen.destroy();
            view.destroy();
          } catch (e) {
            console.warn('createMapView destroy:', e);
          }
        };

        view.when(
          () => {
            // Boost visibility of LPC reference layers from the WebMap
            if (webmapId && view.map.allLayers) {
              view.map.allLayers.forEach(lyr => {
                const t = (lyr.title || '').toLowerCase();
                if (t.includes('historic district') || t.includes('landmark') || t.includes('scenic')) {
                  if (lyr.opacity != null && lyr.opacity < 0.7) lyr.opacity = 0.7;
                }
              });
            }
            resolve({ view, destroy });
          },
          () => resolve({ view, destroy })
        );
      } catch (e) {
        if (retries < 30) setTimeout(tryInit, 100);
        else reject(e);
      }
    };

    tryInit();
  });
}

/**
 * Extract and parse a GeoJSON geometry object from a row returned by the backend.
 * Handles the various column names (geometry, shape, geom, the_geom) and string→object parsing.
 * @param {object} row - { geometry | shape | geom | the_geom }
 * @returns {object|null} parsed GeoJSON geometry or null
 */
export function parseGeomFromRow(row) {
  if (!row) return null;
  let geomData = row.geometry ?? row.shape ?? row.geom ?? row.the_geom;
  if (typeof geomData === 'string') {
    try { geomData = JSON.parse(geomData); } catch { return null; }
  }
  return geomData || null;
}

/**
 * Convert an ArcGIS polygon graphic (e.g. from a Sketch widget) to a GeoJSON
 * Polygon in WGS84. Web Mercator (3857/102100) rings are reprojected inline;
 * WGS84 and unknown SRIDs pass coordinates through unchanged.
 * @param {object} graphic - ArcGIS Graphic with a polygon geometry
 * @returns {object|null} GeoJSON Polygon or null for non-polygon input
 */
export function graphicToGeoJSON(graphic) {
  if (!graphic?.geometry) return null;
  const geom = graphic.geometry;
  if (geom.type !== 'polygon' || !geom.rings) return null;

  let rings;
  const wkid = geom.spatialReference?.wkid || geom.spatialReference?.latestWkid;
  if (wkid === 3857 || wkid === 102100) {
    rings = geom.rings.map(ring =>
      ring.map(pt => {
        const x = (pt[0] * 180) / 20037508.34;
        const y = (Math.atan(Math.exp((pt[1] * Math.PI) / 20037508.34)) * 360) / Math.PI - 90;
        return [x, y];
      })
    );
  } else {
    rings = geom.rings.map(ring => ring.map(pt => [pt[0], pt[1]]));
  }
  return { type: 'Polygon', coordinates: rings };
}

/** Open CycloMedia Street Smart viewer centered on a WGS84 coordinate. */
export function openCycloMedia(lon, lat) {
  const url = `/api/v1/cyclomedia/viewer?lon=${encodeURIComponent(lon)}&lat=${encodeURIComponent(lat)}`;
  window.open(url, '_blank', 'noopener');
}

/** Add a CycloMedia button to the ArcGIS map UI */
export function addCycloMediaButton(view, centroidRef) {
  const btn = document.createElement('button');
  btn.title = 'Open in CycloMedia Street View';
  btn.className = 'esri-widget--button esri-widget esri-interactive';
  btn.style.cssText = 'display:flex;align-items:center;justify-content:center;width:32px;height:32px;cursor:pointer;';
  btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
    <circle cx="12" cy="13" r="4"/>
  </svg>`;
  btn.addEventListener('click', () => {
    const c = centroidRef.current;
    if (c) openCycloMedia(c.x, c.y);
  });
  view.ui.add(btn, 'top-right');
  return btn;
}

/** Add a 1940s.nyc tax photo button to the ArcGIS map UI. Reads BBL from bblRef on click. */
export function addNyc1940sButton(view, bblRef) {
  const btn = document.createElement('button');
  btn.title = 'View on 1940s.nyc';
  btn.className = 'esri-widget--button esri-widget esri-interactive';
  btn.style.cssText = 'display:flex;align-items:center;justify-content:center;width:32px;height:32px;cursor:pointer;';
  btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
    <circle cx="8.5" cy="8.5" r="1.5"/>
    <polyline points="21 15 16 10 5 21"/>
  </svg>`;
  btn.addEventListener('click', () => {
    const url = nyc1940sPhotoUrl(bblRef.current);
    if (url) window.open(url, '_blank', 'noopener');
  });
  view.ui.add(btn, 'top-right');
  return btn;
}

/**
 * Compute centroid from GeoJSON-like geometry.
 * @param {object} geomData - { type, coordinates }
 * @returns {{ x: number, y: number } | null}
 */
export function getCentroid(geomData) {
  try {
    if (geomData.type === 'Point') {
      return { x: geomData.coordinates[0], y: geomData.coordinates[1] };
    }
    if (geomData.type === 'LineString') {
      const mid = Math.floor(geomData.coordinates.length / 2);
      return { x: geomData.coordinates[mid][0], y: geomData.coordinates[mid][1] };
    }
    if (geomData.type === 'Polygon') {
      const ring = geomData.coordinates[0] || [];
      let x = 0, y = 0;
      ring.forEach(coord => { x += coord[0]; y += coord[1]; });
      return ring.length ? { x: x / ring.length, y: y / ring.length } : null;
    }
    if (geomData.type === 'MultiPolygon') {
      let x = 0, y = 0, count = 0;
      geomData.coordinates.forEach(poly => {
        const ring = poly[0] || [];
        ring.forEach(coord => { x += coord[0]; y += coord[1]; count++; });
      });
      return count > 0 ? { x: x / count, y: y / count } : null;
    }
    return null;
  } catch (e) {
    return null;
  }
}

// LPC Official Map Colors (from NYC LPC Color Guide)
// These colors are defined in src/themes/_variables.css as --map-* variables
// Site Highlight Teal: RGB 38, 173, 173 (#26adad)
// Individual Landmark Blue: RGB 58, 98, 173 (#3a62ad)
//
// To change map colors, edit the --map-* variables in _variables.css
// and use mapColors from utils/themeColors.js for dynamic access

// Static fallback symbols (used when CSS vars not yet available)
const FILL_COLOR = [38, 173, 173];
export const PIN_COLOR = [58, 98, 173];

const FILL_SYMBOL = { type: 'simple-fill', color: [...FILL_COLOR, 0.6], outline: { color: FILL_COLOR, width: 3 } };
const LINE_SYMBOL = { type: 'simple-line', color: FILL_COLOR, width: 4 };
const POINT_SYMBOL = { type: 'simple-marker', color: FILL_COLOR, size: 16, outline: { color: [255, 255, 255], width: 3 } };
const PIN_SYMBOL = { type: 'simple-marker', style: 'circle', color: PIN_COLOR, size: 18, outline: { color: [255, 255, 255], width: 3 } };

/**
 * Get dynamic map symbols from CSS theme variables
 * Falls back to static symbols if CSS vars not available
 */
export function getThemedMapSymbols() {
  try {
    return {
      fill: mapColors.fillSymbol,
      line: mapColors.lineSymbol,
      point: mapColors.pointSymbol,
      pin: mapColors.pinSymbol,
      cluster: mapColors.clusterSymbol
    };
  } catch {
    return { fill: FILL_SYMBOL, line: LINE_SYMBOL, point: POINT_SYMBOL, pin: PIN_SYMBOL, cluster: null };
  }
}

/**
 * Draw site geometries on the map. Uses window.require for Graphic, Polygon, Polyline, Point.
 * @param {import('esri/views/MapView').default} view
 * @param {object[]} siteGeoms - each: geometry | shape | geom | the_geom (GeoJSON or string)
 * @param {{ fitBounds?: boolean }} [opts] - fitBounds: default true
 * @returns {Promise<void>}
 */
export async function drawSiteGeometries(view, siteGeoms, opts = {}) {
  const { fitBounds = true, title, layerId } = opts;

  if (!view || !siteGeoms || siteGeoms.length === 0) return;
  if (!window.require) return;

  const modulesToLoad = ['esri/Graphic', 'esri/geometry/Polygon', 'esri/geometry/Polyline', 'esri/geometry/Point'];
  if (title) modulesToLoad.push('esri/layers/GraphicsLayer');

  const [Graphic, Polygon, Polyline, Point, GraphicsLayer] = await Promise.all(
    modulesToLoad.map(loadAMD)
  );

  // When a title is provided, use a dedicated GraphicsLayer so it appears in LayerList.
  // Otherwise fall back to view.graphics for backwards compatibility.
  let graphicsLayer;
  if (title && GraphicsLayer) {
    // Remove any previous site geometry layer (use caller-supplied or default ID)
    const id = layerId || 'site-geometry-layer';
    const existing = view.map.findLayerById(id);
    if (existing) { view.map.remove(existing); existing.destroy(); }
    graphicsLayer = new GraphicsLayer({ id, title });
    view.map.add(graphicsLayer);
  }
  const target = graphicsLayer || view.graphics;
  target.removeAll();
  let bounds = null;

  const addGraphic = (geometry, symbol) => {
    if (geometry && symbol) {
      target.add(new Graphic({ geometry, symbol }));
      if (geometry.extent) bounds = bounds ? bounds.union(geometry.extent) : geometry.extent;
    }
  };

  (siteGeoms || []).forEach((row) => {
    try {
      let geomData = row.geometry ?? row.shape ?? row.geom ?? row.the_geom;
      if (typeof geomData === 'string') geomData = JSON.parse(geomData);
      if (!geomData || !geomData.type) return;

      const spatialRef = (geomData.crs?.properties?.name === 'EPSG:2263') ? { wkid: 2263 } : { wkid: 4326 };

      if (geomData.type === 'MultiPolygon') {
        geomData.coordinates.forEach((poly) => {
          const ring = poly[0];
          if (ring && ring.length) {
            const geometry = new Polygon({ rings: [ring], spatialReference: spatialRef });
            addGraphic(geometry, FILL_SYMBOL);
          }
        });
      } else if (geomData.type === 'Polygon') {
        const geometry = new Polygon({ rings: geomData.coordinates, spatialReference: spatialRef });
        addGraphic(geometry, FILL_SYMBOL);
      } else if (geomData.type === 'LineString') {
        const geometry = new Polyline({ paths: [geomData.coordinates], spatialReference: spatialRef });
        addGraphic(geometry, LINE_SYMBOL);
      } else if (geomData.type === 'Point') {
        const geometry = new Point({ x: geomData.coordinates[0], y: geomData.coordinates[1], spatialReference: spatialRef });
        addGraphic(geometry, POINT_SYMBOL);
      }

      const centroid = getCentroid(geomData);
      if (centroid) {
        const pinGeometry = new Point({ x: centroid.x, y: centroid.y, spatialReference: spatialRef });
        target.add(new Graphic({ geometry: pinGeometry, symbol: PIN_SYMBOL }));
        if (pinGeometry.extent) bounds = bounds ? bounds.union(pinGeometry.extent) : pinGeometry.extent;
      }
    } catch (e) { /* skip invalid geometry */ }
  });

  if (fitBounds && bounds && siteGeoms.length > 0) {
    view.goTo({ target: bounds, padding: { top: 50, left: 50, right: 50, bottom: 50 } });
  }
}

// Project boundary symbol: orange outline, semi-transparent fill (distinct from blue site markers)
const PROJECT_BOUNDARY_SYMBOL = {
  type: 'simple-fill',
  color: [...FILL_COLOR, 0.15],
  outline: { color: FILL_COLOR, width: 3, style: 'dash' }
};

/**
 * Draw project boundary geometry on the map using a dedicated GraphicsLayer.
 * @param {import('esri/views/MapView').default} view
 * @param {object} geomData - GeoJSON geometry { type, coordinates }
 * @param {{ fitBounds?: boolean, clearExisting?: boolean, layer?: { current: any }, geometryId?: number }} [opts]
 *   - layer: a ref object to store the created GraphicsLayer (so caller can remove it later)
 *   - geometryId: sat_project_geometry_id to store as attribute for click-to-delete
 * @returns {Promise<void>}
 */
export async function drawProjectGeometry(view, geomData, opts = {}) {
  const { fitBounds = true, clearExisting = false, layer: layerRef, geometryId, title } = opts;

  if (!view || !geomData) return;
  if (!window.require) return;

  const [Graphic, Polygon, GraphicsLayer] = await Promise.all(
    ['esri/Graphic', 'esri/geometry/Polygon', 'esri/layers/GraphicsLayer'].map(loadAMD)
  );

  // Use a dedicated GraphicsLayer so boundary isn't cleared by other effects
  let graphicsLayer;
  if (layerRef?.current) {
    graphicsLayer = layerRef.current;
  } else {
    graphicsLayer = new GraphicsLayer({ id: 'project-boundary-layer', title: title || 'Project Boundary' });
    view.map.add(graphicsLayer);
    if (layerRef) layerRef.current = graphicsLayer;
  }

  if (clearExisting) graphicsLayer.removeAll();

  let geometry;
  if (geomData.type === 'Polygon') {
    geometry = new Polygon({ rings: geomData.coordinates, spatialReference: { wkid: 4326 } });
  } else if (geomData.type === 'MultiPolygon') {
    const allRings = [];
    geomData.coordinates.forEach(poly => poly.forEach(ring => allRings.push(ring)));
    geometry = new Polygon({ rings: allRings, spatialReference: { wkid: 4326 } });
  }

  if (geometry) {
    const attributes = geometryId != null ? { geometryId } : {};
    graphicsLayer.add(new Graphic({ geometry, symbol: PROJECT_BOUNDARY_SYMBOL, attributes }));
    if (fitBounds && geometry.extent) {
      view.goTo({ target: geometry.extent, padding: { top: 50, left: 50, right: 50, bottom: 50 } });
    }
  }
}

export const MAPPLUTO_DEFAULT = 'https://services5.arcgis.com/GfwWNkhOj9bNBqoJ/arcgis/rest/services/MAPPLUTO/FeatureServer/0';
const BUILDING_FOOTPRINTS_DEFAULT = 'https://services6.arcgis.com/yG5s3afENB5iO9fj/arcgis/rest/services/BUILDING_view/FeatureServer/0';
const BUILDING_CENTROIDS_DEFAULT = 'https://services6.arcgis.com/yG5s3afENB5iO9fj/ArcGIS/rest/services/BUILDING_P_view/FeatureServer/0';
const BUILDING_OUT_FIELDS = ['BIN', 'NAME', 'BASE_BBL', 'MAPPLUTO_BBL', 'CONSTRUCTION_YEAR', 'HEIGHT_ROOF', 'DOITT_ID', 'FEATURE_CODE', 'GEOM_SOURCE', 'GROUND_ELEVATION'];

// Safety backstop for the paging loop: 50 pages * 2000 maxRecordCount = 100k
// features. Real boundaries stay far below this; it only guards against a
// service that keeps reporting exceededTransferLimit indefinitely.
const MAX_QUERY_PAGES = 50;

/**
 * POST an ArcGIS query and page through results until the service stops
 * reporting exceededTransferLimit. Without paging, a single request silently
 * caps at the layer's maxRecordCount (2000 for the NYC layers), dropping the
 * overflow — which is why buildings on large boundaries were being missed.
 *
 * NYC layers advertise supportsPagination. Callers must set a stable
 * orderByFields (OBJECTID) in buildParams — the services' default order shifts
 * features between pages, which would otherwise skip ~7% of them.
 *
 * @param {string} queryUrl - Full FeatureServer layer /query URL
 * @param {() => URLSearchParams} buildParams - Fresh params per page (no resultOffset)
 * @param {boolean} returnGeometry
 * @returns {Promise<{ features: Array<{ attributes: object, geometry?: object }>, exceededLimit: boolean }>}
 */
async function fetchArcGISQueryPaged(queryUrl, buildParams, returnGeometry) {
  const all = [];
  let offset = 0;
  let exceeded = false;

  for (let page = 0; page < MAX_QUERY_PAGES; page++) {
    const params = buildParams();
    if (offset > 0) params.set('resultOffset', String(offset));

    const r = await fetch(queryUrl, { method: 'POST', body: params });
    if (!r.ok) throw new Error(`ArcGIS query failed: ${r.status}`);
    const data = await r.json();
    if (data.error) throw new Error(data.error.message || 'ArcGIS query error');

    const feats = (data.features || []).map(f => ({
      attributes: f.attributes || {},
      ...(returnGeometry && f.geometry ? { geometry: f.geometry } : {})
    }));
    all.push(...feats);

    exceeded = !!data.exceededTransferLimit;
    // Stop once the service reports the full set was returned, or a page comes
    // back empty (defensive — avoids an infinite loop if exceeded stays set).
    if (!exceeded || feats.length === 0) break;
    offset += feats.length;
  }

  // exceededLimit now means "still truncated after MAX_QUERY_PAGES" rather than
  // "truncated at the first 2000" — downstream warnings only fire in that case.
  return { features: all, exceededLimit: exceeded };
}

/**
 * Execute a non-spatial attribute query (WHERE clause) against an ArcGIS FeatureServer URL.
 * Uses fetch POST to avoid URL length limits on large IN (...) clauses.
 */
function fetchFeatureServiceAttributeQuery(url, whereClause, opts = {}) {
  const { outFields = ['*'], returnGeometry = false } = opts;

  const buildParams = () => {
    const params = new URLSearchParams();
    params.set('where', whereClause);
    params.set('outFields', outFields.join(','));
    params.set('outSR', '4326');
    params.set('returnGeometry', String(returnGeometry));
    params.set('returnExceededLimitWarning', 'true');
    // Stable ordering by OID is required for gap-free pagination — the NYC
    // layers' default order shifts features between pages, causing skips.
    params.set('orderByFields', 'OBJECTID');
    params.set('f', 'json');
    return params;
  };

  return fetchArcGISQueryPaged(url + '/query', buildParams, returnGeometry);
}


/**
 * Convert GeoJSON polygon/multipolygon coordinates to Esri JSON rings.
 */
function geoJSONToEsriRings(geometryGeoJSON) {
  if (geometryGeoJSON.type === 'Polygon') {
    return geometryGeoJSON.coordinates;
  } else if (geometryGeoJSON.type === 'MultiPolygon') {
    return geometryGeoJSON.coordinates.flat();
  }
  return null;
}

/**
 * Count total vertices in GeoJSON polygon/multipolygon coordinates.
 */
function countVertices(geometryGeoJSON) {
  let count = 0;
  const coords = geometryGeoJSON.coordinates;
  if (geometryGeoJSON.type === 'Polygon') {
    coords.forEach(ring => { count += ring.length; });
  } else if (geometryGeoJSON.type === 'MultiPolygon') {
    coords.forEach(poly => poly.forEach(ring => { count += ring.length; }));
  }
  return count;
}

// ArcGIS REST API hard limit: geometry with >8000 vertices in a query body
// will return 413 regardless of GET/POST.
const MAX_QUERY_VERTICES = 8000;

/**
 * Execute a spatial intersect query against an ArcGIS FeatureServer URL using
 * fetch POST directly. Rejects with a user-friendly message if the boundary
 * geometry exceeds the ArcGIS REST API vertex limit.
 */
function fetchFeatureServiceQuery(url, geometryGeoJSON, opts = {}) {
  const { outFields = ['*'], returnGeometry = false } = opts;

  const rings = geoJSONToEsriRings(geometryGeoJSON);
  if (!rings) return Promise.resolve({ features: [], exceededLimit: false });

  const vertices = countVertices(geometryGeoJSON);
  if (vertices > MAX_QUERY_VERTICES) {
    return Promise.reject(new Error(
      `Boundary geometry is too complex (${vertices.toLocaleString()} vertices). ` +
      `Please import only the boundary outline, not individual building footprints. ` +
      `The ArcGIS service limit is approximately ${MAX_QUERY_VERTICES.toLocaleString()} vertices.`
    ));
  }

  const esriGeometry = JSON.stringify({
    rings,
    spatialReference: { wkid: 4326 }
  });

  const buildParams = () => {
    const params = new URLSearchParams();
    params.set('where', '1=1');
    params.set('geometry', esriGeometry);
    params.set('geometryType', 'esriGeometryPolygon');
    params.set('spatialRel', 'esriSpatialRelIntersects');
    params.set('inSR', '4326');
    params.set('outSR', '4326');
    params.set('outFields', outFields.join(','));
    params.set('returnGeometry', String(returnGeometry));
    params.set('returnExceededLimitWarning', 'true');
    // Stable ordering by OID is required for gap-free pagination — the NYC
    // layers' default order shifts features between pages, causing skips.
    params.set('orderByFields', 'OBJECTID');
    params.set('f', 'json');
    return params;
  };

  return fetchArcGISQueryPaged(url + '/query', buildParams, returnGeometry);
}

/**
 * Query NYC MAPPLUTO (tax lots) that intersect a boundary.
 * Uses fetch POST to avoid 413 errors on complex boundary geometries.
 * @param {object} geometryGeoJSON - GeoJSON polygon/multipolygon { type, coordinates } (WGS84)
 * @returns {Promise<{ features: Array<{ attributes: object, geometry?: object }>, exceededLimit: boolean }>}
 */
export async function queryMapplutoByBoundary(geometryGeoJSON) {
  if (!geometryGeoJSON?.type || !geometryGeoJSON?.coordinates) {
    return { features: [], exceededLimit: false };
  }
  const url = await getServiceUrl('arcgis_mappluto_url', MAPPLUTO_DEFAULT);
  return fetchFeatureServiceQuery(url, geometryGeoJSON, {
    outFields: ['*'],
    returnGeometry: true
  });
}

/**
 * Generic spatial query against any ArcGIS FeatureServer URL.
 * Uses fetch POST to avoid 413 errors on complex boundary geometries.
 * @param {string} url - FeatureServer layer URL
 * @param {object} geometryGeoJSON - GeoJSON polygon/multipolygon { type, coordinates } (WGS84)
 * @param {{ outFields?: string[], returnGeometry?: boolean }} [opts]
 * @returns {Promise<{ features: Array<{ attributes: object }>, exceededLimit: boolean }>}
 */
export function queryFeatureServiceByBoundary(url, geometryGeoJSON, opts = {}) {
  if (!geometryGeoJSON?.type || !geometryGeoJSON?.coordinates) {
    return Promise.resolve({ features: [], exceededLimit: false });
  }
  return fetchFeatureServiceQuery(url, geometryGeoJSON, opts);
}

/**
 * Query NYC Building Footprints that intersect a boundary.
 * @param {object} geometryGeoJSON - GeoJSON polygon/multipolygon (WGS84)
 * @returns {Promise<{ features: Array<{ attributes: object }>, exceededLimit: boolean }>}
 */
export async function queryBuildingFootprintsByBoundary(geometryGeoJSON) {
  const url = await getServiceUrl('arcgis_building_footprints_url', BUILDING_FOOTPRINTS_DEFAULT);
  return queryFeatureServiceByBoundary(url, geometryGeoJSON, {
    outFields: BUILDING_OUT_FIELDS,
    returnGeometry: false
  });
}

/**
 * Query NYC Building Centroid Points that intersect a boundary.
 * @param {object} geometryGeoJSON - GeoJSON polygon/multipolygon (WGS84)
 * @returns {Promise<{ features: Array<{ attributes: object }>, exceededLimit: boolean }>}
 */
export async function queryBuildingCentroidsByBoundary(geometryGeoJSON) {
  const url = await getServiceUrl('arcgis_building_centroids_url', BUILDING_CENTROIDS_DEFAULT);
  return queryFeatureServiceByBoundary(url, geometryGeoJSON, {
    outFields: BUILDING_OUT_FIELDS,
    returnGeometry: false
  });
}

/**
 * Query NYC Building Footprints by a list of BBL values (attribute query, no geometry filter).
 * Batches BBLs into groups of 200 to avoid URL length limits.
 * @param {string[]} bbls - Array of BBL strings
 * @returns {Promise<{ features: Array<{ attributes: object }>, exceededLimit: boolean }>}
 */
export async function queryBuildingFootprintsByBBLs(bbls) {
  if (!bbls || bbls.length === 0) {
    return { features: [], exceededLimit: false };
  }

  const url = await getServiceUrl('arcgis_building_footprints_url', BUILDING_FOOTPRINTS_DEFAULT);
  const uniqueBBLs = [...new Set(bbls.map(b => String(b).trim()).filter(Boolean))];
  const BATCH_SIZE = 200;
  const batches = [];
  for (let i = 0; i < uniqueBBLs.length; i += BATCH_SIZE) {
    batches.push(uniqueBBLs.slice(i, i + BATCH_SIZE));
  }

  const results = await Promise.all(
    batches.map(batch => {
      const whereClause = `MAPPLUTO_BBL IN (${batch.map(b => `'${b}'`).join(',')})`;
      return fetchFeatureServiceAttributeQuery(url, whereClause, {
        outFields: BUILDING_OUT_FIELDS,
        returnGeometry: false
      });
    })
  );
  const allFeatures = results.flatMap(r => r.features);
  const anyExceeded = results.some(r => r.exceededLimit);
  return { features: allFeatures, exceededLimit: anyExceeded };
}

/**
 * Draw site geometries with clustering for large datasets.
 * Uses FeatureLayer with featureReduction for efficient rendering of many points.
 * @param {import('esri/views/MapView').default} view
 * @param {object[]} siteGeoms - each: { hub_site_id, geometry } (GeoJSON or string)
 * @param {{ fitBounds?: boolean, onProgress?: (pct: number) => void }} [opts]
 * @returns {Promise<{ layer: any, destroy: () => void }>}
 */
export async function drawSiteGeometriesClustered(view, siteGeoms, opts = {}) {
  const { fitBounds = true, onProgress, title } = opts;

  if (!view || !siteGeoms || siteGeoms.length === 0) {
    return { layer: null, destroy: () => {} };
  }
  if (!window.require) return { layer: null, destroy: () => {} };

  const [FeatureLayer, Point, Graphic, Extent] = await Promise.all(
    ['esri/layers/FeatureLayer', 'esri/geometry/Point', 'esri/Graphic', 'esri/geometry/Extent'].map(loadAMD)
  );

  if (onProgress) onProgress(10);

  // Convert geometries to point graphics (using centroids)
  const graphics = [];
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

  siteGeoms.forEach((row, idx) => {
    try {
      let geomData = row.geometry ?? row.shape ?? row.geom ?? row.the_geom;
      if (typeof geomData === 'string') geomData = JSON.parse(geomData);
      if (!geomData || !geomData.type) return;

      const centroid = getCentroid(geomData);
      if (!centroid) return;

      if (centroid.x < minX) minX = centroid.x;
      if (centroid.x > maxX) maxX = centroid.x;
      if (centroid.y < minY) minY = centroid.y;
      if (centroid.y > maxY) maxY = centroid.y;

      const point = new Point({
        x: centroid.x,
        y: centroid.y,
        spatialReference: { wkid: 4326 }
      });

      graphics.push(new Graphic({
        geometry: point,
        attributes: {
          ObjectID: idx,
          hub_site_id: row.hub_site_id || idx,
          name: row.name || `Building ${row.hub_site_id || idx}`
        }
      }));
    } catch (e) { /* skip invalid */ }
  });

  if (onProgress) onProgress(50);

  if (graphics.length === 0) return { layer: null, destroy: () => {} };

  const layer = new FeatureLayer({
    title: title || 'Buildings',
    source: graphics,
    objectIdField: 'ObjectID',
    fields: [
      { name: 'ObjectID', type: 'oid' },
      { name: 'hub_site_id', type: 'integer' },
      { name: 'name', type: 'string' }
    ],
    outFields: ['*'],
    renderer: {
      type: 'simple',
      symbol: {
        type: 'simple-marker',
        style: 'circle',
        color: [...PIN_COLOR, 0.8],
        size: '12px',
        outline: { color: [255, 255, 255], width: 1.5 }
      }
    },
    popupEnabled: false
  });

  view.map.add(layer);
  if (onProgress) onProgress(80);

  if (fitBounds && minX !== Infinity) {
    const extent = new Extent({
      xmin: minX, ymin: minY, xmax: maxX, ymax: maxY,
      spatialReference: { wkid: 4326 }
    });
    view.goTo(extent.expand(1.2)).catch(() => {});
  }

  if (onProgress) onProgress(100);

  const destroy = () => {
    try {
      view.map.remove(layer);
      layer.destroy();
    } catch (e) {
      console.warn('Clustered layer destroy:', e);
    }
  };

  return { layer, destroy };
}
